import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUserContext } from '../../context/users/UserContext'
import { 
  FaFolderOpen, 
  FaPen, 
  FaTimes, 
  FaSearch, 
  FaArrowLeft,
  FaExclamationTriangle,
  FaCheckCircle
} from 'react-icons/fa';
import {
    categoryHaveFilesRelated,
    deleteCategory,
    getCategories,
    updateCategory
} from "../../context/photos/PhotoActions";
import { Card, Row, Col, Button, Form, Modal, Badge } from 'react-bootstrap'
import './Category.css'

// eslint-disable-next-line
export default function Category(props) {
    const navigate = useNavigate()
    const { setAlert } = useUserContext()

    const [categories, setCategories] = useState([])
    const [filteredCategories, setFilteredCategories] = useState([])
    const [searchTerm, setSearchTerm] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [categoryToDelete, setCategoryToDelete] = useState(null)

    window.scrollTo({
        top: 0,
        behavior: 'auto',
    })

    useEffect(() => {
        const getPictures = async () => {
            setIsLoading(true)
            try {
                const categoryResponse = await getCategories();
                const categoriesWithEditing = categoryResponse.map(category => ({
                    ...category,
                    editing: false
                }));
                setCategories(categoriesWithEditing);
                setFilteredCategories(categoriesWithEditing);
            } catch (error) {
                setAlert({
                    message: 'Error al cargar las categorías',
                    msgType: 'warning'
                })
            } finally {
                setIsLoading(false)
            }
        }

        getPictures()
    }, [])

    // Filter categories based on search term
    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredCategories(categories);
        } else {
            const filtered = categories.filter(category =>
                category.categoryName.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredCategories(filtered);
        }
    }, [searchTerm, categories])


    // const handleSearch = async (e) => {
    //     const value = e.target.value
    //     if(value.length > 2 ){
    //         const categoryResponse = await getCategories(value)
    //         setCategories(categoryResponse)
    //     }else if(value.length === 0 ){
    //         const categoryResponse = await getCategories();
    //         setCategories(categoryResponse)
    //     }
    // }

    const handleSearch = (e) => {
        setSearchTerm(e.target.value)
    }

    const handleChangeCategoryName = async (id, e) => {
        const value = e.target.value.trim()
        if(value.length > 2 ){
            try {
                const response = await updateCategory(id, value);
                if(Object.prototype.hasOwnProperty.call(response, 'response')) {
                    setAlert({
                        message: `Categoría actualizada exitosamente`,
                        msgType: 'success'
                    })

                    const categoryResponse = await getCategories();
                    const categoriesWithEditing = categoryResponse.map(category => ({
                        ...category,
                        editing: false
                    }));
                    setCategories(categoriesWithEditing);
                } else {
                    setAlert({
                        message: response.error || response,
                        msgType: 'warning'
                    })
                }
            } catch (error) {
                setAlert({
                    message: 'Error al actualizar la categoría',
                    msgType: 'warning'
                })
            }
        }
    }

    const handleDeleteClick = (category) => {
        setCategoryToDelete(category);
        setShowDeleteModal(true);
    }

    const handleCloseModal = () => {
        setShowDeleteModal(false);
        setCategoryToDelete(null);
    }

    const confirmDelete = async () => {
        if (!categoryToDelete) return;

        try {
            let hasFilesRelated = await categoryHaveFilesRelated(categoryToDelete.id);

            if(hasFilesRelated){
                setAlert({
                    message: 'No se puede borrar la categoría porque tiene fotografías asociadas.',
                    msgType: 'warning'
                })
                handleCloseModal();
                return;
            }

            let response = await deleteCategory(categoryToDelete.id)

            if(Object.prototype.hasOwnProperty.call(response, 'response')) {
                setAlert({
                    message: `Categoría borrada exitosamente`,
                    msgType: 'success'
                })
                const categoryResponse = await getCategories();
                const categoriesWithEditing = categoryResponse.map(category => ({
                    ...category,
                    editing: false
                }));
                setCategories(categoriesWithEditing);
            } else {
                setAlert({
                    message: response.error || response,
                    msgType: 'warning'
                })
            }
        } catch (error) {
            setAlert({
                message: 'Error al eliminar la categoría',
                msgType: 'warning'
            })
        } finally {
            handleCloseModal();
        }
    }

    const handleEditCategory = (categoryId) => {
        const updatedCategories = categories.map(category => {
            if (category.id === categoryId) {
                return { ...category, editing: true };
            } else {
                return { ...category, editing: false };
            }
        });

        setCategories(updatedCategories);
    };

    const handleGoBack = () => {
        navigate('/fotografias')
    }

    return (
        <div className="categories-container">
            <div className="container">
                {/* Header Section */}
                <div className="categories-header">
                    <div className="header-content">
                        <div className="header-icon">
                            <FaFolderOpen />
                        </div>
                        <div className="header-text">
                            <h1>Categorías</h1>
                            <p>Gestiona las categorías de fotografías del sistema</p>
                        </div>
                    </div>
                </div>

                {/* Actions Card */}
                <Card className="actions-card">
                    <Card.Body>
                        <Row className="align-items-center">
                            <Col lg={3} md={6} sm={12} className="mb-3 mb-lg-0">
                                <Button 
                                    variant="outline-secondary" 
                                    className="back-button"
                                    onClick={handleGoBack}
                                >
                                    Volver a Fotografías
                                </Button>
                            </Col>
                            <Col lg={9} md={6} sm={12}>
                                <div className="search-input-container">
                                    <FaSearch className="search-icon" />
                                    <Form.Control
                                        type="text"
                                        placeholder="Buscar categorías..."
                                        value={searchTerm}
                                        onChange={handleSearch}
                                        className="search-input"
                                    />
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Table Card */}
                <Card className="table-card">
                    <Card.Body>
                        {isLoading ? (
                            <div className="loading-container">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Cargando...</span>
                                </div>
                                <p>Cargando categorías...</p>
                            </div>
                        ) : filteredCategories.length === 0 ? (
                            <div className="empty-state">
                                <FaFolderOpen className="empty-icon" />
                                <h4>No se encontraron categorías</h4>
                                <p>{searchTerm ? 'No hay categorías que coincidan con tu búsqueda.' : 'No hay categorías registradas en el sistema.'}</p>
                            </div>
                        ) : (
                            <>
                                {/* Desktop Table View */}
                                <div className="d-none d-lg-block">
                                    <table className="categories-table">
                                        <thead>
                                            <tr>
                                                <th>Id</th>
                                                <th>Nombre de Categoría</th>
                                                <th>Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredCategories.map((category, index) => (
                                                <tr key={category.id} className="category-row">
                                                    <td className="category-id">#{category.id}</td>
                                                    <td className="category-name">
                                                        <Form.Control
                                                            type="text"
                                                            defaultValue={category.categoryName}
                                                            placeholder="Nombre de categoría"
                                                            disabled={!category.editing}
                                                            onBlur={(e) => handleChangeCategoryName(category.id, e)}
                                                            className={`category-input ${category.editing ? 'editing' : ''}`}
                                                        />
                                                    </td>
                                                    <td className="category-actions">
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={() => handleEditCategory(category.id)}
                                                            className="edit-btn"
                                                            title="Editar categoría"
                                                        >
                                                            <FaPen />
                                                        </Button>
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={() => handleDeleteClick(category)}
                                                            className="delete-btn"
                                                            title="Eliminar categoría"
                                                        >
                                                            <FaTimes />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Mobile Cards View */}
                                <div className="d-lg-none">
                                    <div className="categories-cards">
                                        {filteredCategories.map((category, index) => (
                                            <div key={category.id} className="category-card">
                                                <div className="category-card-content">
                                                    <div className="category-info">
                                                        <div className="category-avatar">
                                                            <span className="avatar-text">#{category.id}</span>
                                                        </div>
                                                        <div className="category-details">
                                                            <Form.Control
                                                                type="text"
                                                                defaultValue={category.categoryName}
                                                                placeholder="Nombre de categoría"
                                                                disabled={!category.editing}
                                                                onBlur={(e) => handleChangeCategoryName(category.id, e)}
                                                                className={`category-input-mobile ${category.editing ? 'editing' : ''}`}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="category-actions-mobile">
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={() => handleEditCategory(category.id)}
                                                            className="edit-btn-mobile"
                                                            title="Editar categoría"
                                                        >
                                                            <FaPen />
                                                        </Button>
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={() => handleDeleteClick(category)}
                                                            className="delete-btn-mobile"
                                                            title="Eliminar categoría"
                                                        >
                                                            <FaTimes />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}
                    </Card.Body>
                </Card>

                {/* Delete Confirmation Modal */}
                <Modal show={showDeleteModal} onHide={handleCloseModal} centered className="delete-modal">
                    <Modal.Header closeButton className="delete-modal-header">
                        <Modal.Title className="delete-modal-title">
                            <div className="modal-icon-container">
                                <FaExclamationTriangle className="modal-warning-icon" />
                            </div>
                            <div>
                                <h5 className="modal-title-text">Confirmar Eliminación</h5>
                                <p className="modal-subtitle">Esta acción es permanente</p>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    
                    <Modal.Body className="delete-modal-body">
                        {categoryToDelete && (
                            <div className="category-preview">
                                <h6 className="preview-label">Categoría a eliminar:</h6>
                                <div className="category-content">
                                    <div className="category-avatar-preview">
                                        <span className="avatar-text">#{categoryToDelete.id}</span>
                                    </div>
                                    <div className="category-details-preview">
                                        <p className="category-text">
                                            <strong>Nombre:</strong> {categoryToDelete.categoryName}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        <div className="warning-message">
                            <FaExclamationTriangle className="warning-icon" />
                            <p>¿Estás seguro de que quieres eliminar esta categoría? Esta acción es permanente y no se puede deshacer.</p>
                        </div>
                    </Modal.Body>
                    
                    <Modal.Footer className="delete-modal-footer">
                        <Button 
                            variant="outline-secondary" 
                            onClick={handleCloseModal}
                            className="cancel-delete-btn"
                        >
                            Cancelar
                        </Button>
                        <Button 
                            variant="danger" 
                            onClick={confirmDelete}
                            className="confirm-delete-btn"
                        >
                            <FaTimes className="me-2" />
                            Eliminar Categoría
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </div>
    )
}
